import {Notification} from 'letrus-ui';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useMobile} from 'utils/hooks';
import moment from 'moment';
import useNotification, {
  DROPDOWN_NOTIFICATION_DESCRIPTION_MAX_NUMBER_OF_CHARACTERS,
  NOTIFICATION_TITLE_MAX_NUMBER_OF_CHARACTERS,
} from 'features/useNotification';
import 'moment/locale/pt-br';
import {NotificationListProps} from 'letrus-ui/dist/components/features/Notification/NotificationList';
import {
  fetchNotificationRequest,
  updateNotificationById,
} from 'APIs/letrusAPI/notification';
import {NotificationLog} from 'APIs/letrusAPI/notification/types';

moment.locale('pt-br');

function Notifications() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [
    shouldShowOnlyUnseenNotifications,
    setShouldShowOnlyUnseenNotifications,
  ] = useState(false);

  const navigate = useNavigate();
  const {isMobile} = useMobile();

  const {data: notificationData} = fetchNotificationRequest();
  const {getLinkType, hasBeenSeen, getNotificationSeenState} =
    useNotification();

  const notificationList: NotificationListProps['items'] =
    notificationData?.results?.slice(0, 5)?.map((notification) => {
      const time = moment(notification.created).fromNow();

      return {
        id: notification.id,
        title:
          notification.title?.substring(
            0,
            NOTIFICATION_TITLE_MAX_NUMBER_OF_CHARACTERS,
          ) ?? '',
        description: notification.content.substring(
          0,
          DROPDOWN_NOTIFICATION_DESCRIPTION_MAX_NUMBER_OF_CHARACTERS,
        ),
        time,
        opened: hasBeenSeen(notification),
        handleClick: () => onClickNotification(notification),
        withHeaderIcon: !!notification.link,
        hasReceivedToday: false,
      };
    }) || [];

  async function onClickNotification(notification: NotificationLog) {
    const notificationSeenState = getNotificationSeenState(notification);
    const notificationLinkType = getLinkType(notification);

    if (notificationSeenState === 'not-received') {
      await updateNotificationById(notification.id, {
        received: new Date().toISOString(),
      });
    }

    if (notificationSeenState === 'not-opened') {
      await updateNotificationById(notification.id, {
        opened: new Date().toISOString(),
      });
    }

    if (!notification.link) {
      return;
    }

    if (notificationLinkType === 'internal') {
      navigate(notification.link);
    }

    if (notificationLinkType === 'external') {
      window.open(notification.link, '_blank');
    }
  }

  function onClickNotificationHistory() {
    setIsDropdownOpen(false);
    navigate('/notificacoes');
  }

  function onClickNotificationButton() {
    onClickNotificationHistory(); // TODO: remove this line after lib component is fixed

    if (isMobile) {
      onClickNotificationHistory();
    } else {
      setIsDropdownOpen((prev) => !prev);
    }

    const notReceivedYetNotifications = notificationData?.results?.filter(
      (notification) => !notification.received,
    );

    if (notReceivedYetNotifications?.length) {
      Promise.all(
        notReceivedYetNotifications.map((notification) =>
          updateNotificationById(notification.id, {
            received: new Date().toISOString(),
          }),
        ),
      );
    }
  }

  return (
    <div className="Notifications">
      <Notification.Dropdown
        notificationState={
          notificationData?.not_seen_by_teacher ? 'new' : 'none'
        }
        onClickSeeMore={onClickNotificationHistory}
        isDropdownOpen={isDropdownOpen}
        onClickNotificationButton={onClickNotificationButton}
        onClickOutsideDropdown={() => setIsDropdownOpen((prev) => !prev)}
        notificationCount={notificationData?.not_seen_by_teacher || 0}
        onClickFilterUnseenNotifications={() => {
          setShouldShowOnlyUnseenNotifications(
            !shouldShowOnlyUnseenNotifications,
          );
        }}
      >
        {!!notificationData?.results?.length && (
          <Notification.List
            items={
              shouldShowOnlyUnseenNotifications
                ? notificationList.filter((item) => !item.opened)
                : notificationList
            }
          />
        )}
      </Notification.Dropdown>
    </div>
  );
}

export default Notifications;
